import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { AnonymousLayout } from "@/layouts/AnonymousLayout/AnonymousLayout";
import { ChangePasswordForm } from "@/modules/form/components/ChangePasswordForm/ChangePasswordForm";
import { anonymousRouting } from "@/modules/router/routings/anonymousRouting";
import { AnonymousPageBox } from "@/modules/theme/components/AnonymousPageBox";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { createResetPasswordStyles } from "./createResetPasswordStyles";
import { useResetPasswordMutation } from "./useResetPasswordMutation";

const translationNamespace = "pages/ResetPasswordPage";

export function ResetPasswordPage() {
  const history = useHistory();
  const classes = createResetPasswordStyles(useTheme());
  const query = anonymousRouting.useRouteQuery("resetPassword");
  const { t } = useTranslation(translationNamespace);
  const resetPasswordMutation = useResetPasswordMutation();
  const token = query?.token || "";

  const onValidSubmit = useCallback(
    ({ newPassword }: { newPassword: string }) => {
      resetPasswordMutation.mutate({
        newPassword,
        token,
      });
    },
    [token, resetPasswordMutation]
  );

  useEffect(() => {
    if (!token) {
      history.replace(anonymousRouting.buildUrl("signIn"));
    }
  }, [history, token]);

  return (
    <AnonymousLayout>
      <AnonymousPageBox>
        <PageMeta title={t("Reset password")} />
        <div className={classes.textWrapper}>
          <Typography variant="heading" className={classes.heading}>
            {t("Reset password")}
          </Typography>
          <Typography variant="contentMedium">
            {t(
              "Enter a new password and confirm it. Remember the new password must be different from the old one."
            )}
          </Typography>
          <Typography
            variant="contentSmall"
            className={classes.passwordValidationInfo}
          >
            {t(
              "The password should be at least 6 characters long, have min. 1 uppercase letter, 1 digit and 1 special character."
            )}
          </Typography>
        </div>
        <ChangePasswordForm
          isLoading={resetPasswordMutation.status === "loading"}
          onValidSubmit={onValidSubmit}
          mode="reset"
        />
      </AnonymousPageBox>
    </AnonymousLayout>
  );
}
